import './TournamentParticipants.css'
import { useState, useEffect, useCallback, memo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useDispatch, useSelector } from 'react-redux'

import { findPlayers, checkEquality } from '../../generalExternalFunctions'
import {
	useAddSinglePlayersMutation,
	useRemoveParticipantMutation,
	useAddDoublesMutation,
	useRemoveDoubleMutation,
	useSeedDoublesMutation,
	useSeedParticipantsMutation
} from '../../redux/tournamentParticipantsService/tournamentParticipantsApiSlice'
import { setParticipants } from '../../redux/tournamentParticipantsService/tournamentParticipantsSlice'
import { useGetPlayersQuery } from '../../redux/playersService/playersApiSlice'
import { ReactComponent as ButtonDelete } from '../../icon/minus-delete.svg'
import { Button } from '../../components/Button/Button'
import { Popup } from '../../components/Popup/Popup'
import { ButtonWhite } from '../../components/ButtonWhite/ButtonWhite'
import { DoublesearchForPlayers } from '../DoublesearchForPlayers/DoublesearchForPlayers'
import { SinglePlayersSearch } from '../SinglePlayersSearch/SinglePlayersSearch'

import { checkParticipantsOrder, formArraysForRequests } from './externalFunctions'

const TournamentParticipantsComponent = ({ tournament, auth, tournamentRefetch }) => {
	const [addSinglePlayers] = useAddSinglePlayersMutation()
	const [removeParticipant] = useRemoveParticipantMutation()
	const [addDouble] = useAddDoublesMutation()
	const [removeDouble] = useRemoveDoubleMutation()
	const [seedSinglePlayers] = useSeedParticipantsMutation()
	const [seedDoubles] = useSeedDoublesMutation()
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { tournamentUid } = useParams()
	const communityUid = localStorage.getItem('communityUid')
	const participants = useSelector(state => state.participants.participants)
	const {
		data: communityPlayers = [],
		isLoading
	} = useGetPlayersQuery({ community_uid: communityUid }, { skip: !auth })
	const tournamentType = tournament?.type

	const [searchPlayersList, setSearchPlayersList] = useState([])
	const [popupIsOpen, setPopupIsOpen] = useState(false)
	const [immutableState, setImmutableState] = useState([])
	const [showButtonSaveList, setShowButtonSaveList] = useState(false)
	const [involvedPlayers, setInvolvedPlayers] = useState([])

	useEffect(() => {
		if (Object.keys(tournament).length !== 0) {
			setImmutableState(tournament?.players ? tournament?.players : tournament?.doubles)
			dispatch(setParticipants(tournament?.players ? tournament?.players : tournament?.doubles))
		}
	}, [tournament, dispatch])

	useEffect(() => {
		if (communityPlayers?.length > 0) {
			setSearchPlayersList(communityPlayers)
		}
	}, [communityPlayers])

	useEffect(() => {
		const isEqual = checkEquality(participants, immutableState)

		if (isEqual) {
			setShowButtonSaveList(false)
		} else {
			setShowButtonSaveList(true)
		}

		const involved = []

		participants?.forEach(p => {
			involved?.push(p?.player1?.uid)
			involved?.push(p?.player2?.uid)
		})

		setInvolvedPlayers(involved)
	}, [participants, immutableState])

	const showPopup = useCallback(() => {
		if (participants?.length > 7 || tournament?.stages_count) {
			return
		}

		setPopupIsOpen(true)

		document.body.style.overflow = 'hidden'
	}, [participants, tournament?.stages_count])

	const closePopup = useCallback(() => {
		setPopupIsOpen(false)

		document.body.style.overflow = 'auto'
	}, [])

	const search = useCallback((value) => {
		if (value !== '') {
			const foundPlayers = findPlayers(value, communityPlayers)

			setSearchPlayersList(foundPlayers)
		} else {
			setSearchPlayersList(communityPlayers)
		}
	}, [communityPlayers])

	const onDragEnd = (result) => {
		if (!auth || !result.destination) {
			return
		}

		const updatedParticipants = Array.from(participants)
		const [removed] = updatedParticipants.splice(result.source.index, 1)
		updatedParticipants.splice(result.destination.index, 0, removed)
		dispatch(setParticipants(updatedParticipants))
	}

	const removeParticipantFromList = (participant) => {
		const updatedList = participants?.filter(p => participant?.uid !== p?.uid)

		dispatch(setParticipants(updatedList))
	}

	const handleSaveListButton = async () => {
		const { added, removed } = formArraysForRequests(participants, immutableState, tournamentType)
		let state = [...immutableState]
		let updates = [...participants]

		if (added?.length > 0) {
			const newParticipants = await includeParticipant(added)

			if (tournamentType === 'doubles') {
				updates = updates?.map(el => {
					const found = newParticipants?.find(p => el?.uid !== p?.uid &&
						el?.player1?.uid === p?.player1_uid && el?.player2?.uid === p?.player2_uid)

					if (found) {
						const updatedParticipant = { ...el, uid: found?.uid }
						state?.push(updatedParticipant)

						return updatedParticipant
					} else {
						return el
					}
				})
			}
		}

		if (removed?.length > 0) {
			await excludeParticipant(removed)

			state = state?.filter(el => {
				const found = removed.find(item => el?.uid !== item)

				return found
			})
		}

		const { orderIsChanged, seed } = checkParticipantsOrder(updates, state, tournamentType)

		if (orderIsChanged) {
			await saveSeed(seed)
		}

		tournamentRefetch()
	}

	const includeParticipant = async (newPlayers) => {
		try {
			let body = null
			let response = {}

			if (tournamentType === 'singles') {
				body = {
					'players': newPlayers
				}

				response = await addSinglePlayers({ tournament_uid: tournamentUid, body: body }).unwrap()

			} else if (tournamentType === 'doubles') {
				body = {
					'doubles': newPlayers
				}

				response = await addDouble({ tournament_uid: tournamentUid, body: body }).unwrap()
			}

			if (response?.error) {
				console.log('Include participant return error', response?.error)

				return
			}

			return response
		} catch (e) {
			console.log('Include participant catch error', e)
		}
	}

	const excludeParticipant = async (removedPlayers) => {
		try {
			let body = null
			let response = {}

			if (tournamentType === 'singles') {
				body = {
					'players': removedPlayers
				}

				response = await removeParticipant({
					tournament_uid: tournamentUid,
					body: body
				}).unwrap()
			} else if (tournamentType === 'doubles') {
				body = {
					'doubles': removedPlayers
				}

				response = await removeDouble({
					tournament_uid: tournamentUid,
					body: body
				}).unwrap()
			}

			if (response?.error) {
				console.log('Exclude participant return error', response?.error)

				return
			}

			return response
		} catch (e) {
			console.log('Exclude participant catch error', e)
		}
	}

	const saveSeed = async (seed) => {
		try {
			let body = null
			let response = {}

			if (tournamentType === 'singles') {
				body = {
					'players': seed
				}

				response = await seedSinglePlayers({ tournament_uid: tournamentUid, body: body }).unwrap()

			} else if (tournamentType === 'doubles') {
				body = {
					'doubles': seed
				}

				response = await seedDoubles({ tournament_uid: tournamentUid, body: body }).unwrap()
			}

			if (response?.error) {
				console.log('Seed participant return error', response?.error)

				return
			}

			return response?.doubles
		} catch (e) {
			console.log('Seed participant catch error', e)
		}
	}

	return (
		<>
			<div
				className="tournament__participants-section"
				style={{
					marginBottom: showButtonSaveList ?
						'140px'
						:
						'80px'
				}}
			>
				<div className="tournament__participants-header">
					<p className="tournament__participants-title">
						{t('Saved')} {`(${tournament?.participants_count})`}
					</p>

					{
						popupIsOpen && auth &&
						<Popup title={'Add participant'} onClose={() => closePopup(false)} close={true}>
							{
								tournamentType === 'singles' &&
								<SinglePlayersSearch
									search={search}
									searchPlayersList={searchPlayersList}
									participants={participants}
								/>
							}

							{
								tournamentType === 'doubles' &&
								<DoublesearchForPlayers
									search={search}
									searchPlayersList={searchPlayersList}
									participants={participants}
									participans={participants}
									involvedPlayers={involvedPlayers}
								/>
							}
						</Popup>
					}
				</div>

				{
					auth &&
					<div className="participants__button-addition">
						<ButtonWhite
							title={t('Add participant')}
							onClick={showPopup}
							disabled={participants?.length > 7 || tournament?.stages_count > 0 ? true : false || communityPlayers?.length === 0}
						/>
					</div>
				}

				{
					!isLoading && communityPlayers?.length === 0 &&
						<p className="nocontent__text" style={{ margin: '20px 0 0 0' }}>
							{ t('You cannot add participants to the tournament yet') }. <br/>
							{t('Go to')} <Link to="/account" style={{ color: '#367BCF', fontWeight: '500' }}>{t('Account')}</Link> {t('and add players')}
						</p>
				}

				<div className="participants">
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId="participants--singles">
							{
								(provided) => (
									<div
										className="participants--singles"
										{...provided.droppableProps}
										ref={provided.innerRef}
									>
										{
											participants?.map((el, index) =>
												<Draggable
													key={el?.uid}
													draggableId={el?.uid}
													index={index}
													isDragDisabled={tournament?.stages_count > 0 ? true : false}
												>
													{
														(provided, snapshot) => (
															<div
																className="participants-item"
																ref={provided.innerRef}
																{...provided.draggableProps}
																{...provided.dragHandleProps}
																style={{
																	userSelect: 'none',
																	backgroundColor: snapshot.isDragging ?
																		'#F5FAFF'
																		:
																		'transparent',
																	...provided.draggableProps.style
																}}
															>
																{
																	tournamentType === 'singles' ?
																		<>
																			<div className="participants__raiting">
																				<p className="participants__number">
																					{index + 1}
																				</p>
																			</div>

																			<p className="participants__name">
																				{`${el?.last_name} ${el?.first_name}`}
																			</p>

																			{
																				auth && tournament?.stages_count < 1 &&
																				<div>
																					<ButtonDelete
																						className="icon-deletion"
																						onClick={() => removeParticipantFromList(el)}
																					/>
																				</div>
																			}
																		</>
																		:
																		<>
																			<div className="participants__raiting">
																				<p className="participants__number">
																					{index + 1}
																				</p>
																			</div>

																			<div className="participants__names">
																				<p className="participants__palyer--first">
																					{el?.player1?.last_name} {el?.player1?.first_name}
																				</p>

																				<p className="participants__palyer--second">
																					{el?.player2?.last_name} {el?.player2?.first_name}
																				</p>
																			</div>

																			{
																				auth && tournament?.stages_count < 1 &&
																				<div className="participants-icons">
																					<ButtonDelete
																						className="icon-deletion"
																						onClick={() => removeParticipantFromList(el)}
																					/>
																				</div>
																			}
																		</>
																}
															</div>
														)
													}
												</Draggable>
											)
										}

										{provided.placeholder}
									</div>
								)
							}
						</Droppable>
					</DragDropContext>
				</div>

				<div className="participants__button-save">
					{
						showButtonSaveList &&
						<Button onClick={handleSaveListButton} title={t('Save')} />
					}
				</div>
			</div>
		</>
	)
}

export const TournamentParticipants = memo(TournamentParticipantsComponent)